import { useEffect, useState } from 'react';
import { api } from '../lib/api';

const TABS = [
  { id: 'mine',   label: 'My videos' },
  { id: 'pros',   label: 'Pro library' },
  { id: 'upload', label: 'Upload new' }
];

export default function VideoPicker({ slot, onClose, onPick }) {
  const [tab, setTab] = useState('mine');
  const [videos, setVideos] = useState([]);
  const [pros, setPros] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [v, p] = await Promise.all([api.listVideos(), api.listPros()]);
        setVideos(v);
        setPros(p);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-fairway-900 border border-fairway-700 rounded-lg w-full max-w-3xl max-h-[80vh] flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="px-5 py-3 border-b border-fairway-800 flex justify-between items-center">
          <div>
            <h2 className="display text-lg text-cream-100">Pick a video for slot {slot}</h2>
            <p className="text-[11px] text-ink-400">Choose from your uploads, a pro, or upload something new.</p>
          </div>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-100 text-xl leading-none">×</button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-fairway-800">
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`px-4 py-2 text-[11px] uppercase tracking-wider transition ${
                tab === t.id
                  ? 'text-cream-100 border-b-2 border-cream-200'
                  : 'text-ink-400 hover:text-ink-200'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-ink-400 text-sm p-8 text-center">Loading…</div>
          ) : tab === 'mine' ? (
            <MyVideos videos={videos} onPick={onPick} onRefresh={async () => setVideos(await api.listVideos())} />
          ) : tab === 'pros' ? (
            <ProList pros={pros} onPick={onPick} />
          ) : (
            <UploadTab onUploaded={(v) => { setVideos([v, ...videos]); onPick(v); }} />
          )}
        </div>
      </div>
    </div>
  );
}

function MyVideos({ videos, onPick, onRefresh }) {
  if (videos.length === 0) {
    return (
      <div className="text-center p-8 text-ink-400 text-sm">
        No uploads yet. Switch to the "Upload new" tab.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {videos.map(v => (
        <div
          key={v.id}
          className="bg-fairway-950 border border-fairway-800 rounded overflow-hidden hover:border-cream-300 transition cursor-pointer group"
          onClick={() => onPick(v)}
        >
          <div className="aspect-[9/16] bg-black flex items-center justify-center relative">
            <video
              src={api.streamUrl(v.r2_key)}
              preload="metadata"
              muted
              className="w-full h-full object-contain opacity-80 group-hover:opacity-100 transition"
            />
            <div className="absolute bottom-1 right-1 px-1.5 py-0.5 bg-black/70 rounded text-[10px] monospace text-ink-200">
              {v.duration_seconds ? v.duration_seconds.toFixed(1) + 's' : '—'}
            </div>
          </div>
          <div className="p-2">
            <div className="text-xs text-ink-100 truncate">{v.label || v.original_filename || 'Untitled'}</div>
            <div className="text-[10px] text-ink-500 flex gap-2 mt-0.5">
              {v.club && <span>{v.club}</span>}
              {v.view_angle && <span>· {v.view_angle}</span>}
              <span className="ml-auto">{new Date(v.created_at).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ProList({ pros, onPick }) {
  if (pros.length === 0) {
    return (
      <div className="text-center p-8 text-ink-400 text-sm">
        No pro videos in the library yet. Admin can add them from the Library page.
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
      {pros.map(p => (
        <div
          key={p.id}
          className="bg-fairway-950 border border-fairway-800 rounded overflow-hidden hover:border-cream-300 transition cursor-pointer group"
          onClick={() => onPick(p)}
        >
          <div className="aspect-[9/16] bg-black flex items-center justify-center">
            <video
              src={api.streamUrl(p.r2_key)}
              preload="metadata"
              muted
              className="w-full h-full object-contain opacity-80 group-hover:opacity-100 transition"
            />
          </div>
          <div className="p-2">
            <div className="text-xs text-cream-100 truncate font-medium">{p.player_name}</div>
            <div className="text-[10px] text-ink-400 flex gap-2 mt-0.5">
              {p.club && <span>{p.club}</span>}
              {p.view_angle && <span>· {p.view_angle}</span>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function UploadTab({ onUploaded }) {
  const [file, setFile] = useState(null);
  const [label, setLabel] = useState('');
  const [club, setClub] = useState('');
  const [view, setView] = useState('');
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState('');

  const detectDuration = async (file) => {
    return new Promise((resolve) => {
      const v = document.createElement('video');
      v.preload = 'metadata';
      v.src = URL.createObjectURL(file);
      v.onloadedmetadata = () => {
        URL.revokeObjectURL(v.src);
        resolve(v.duration);
      };
      v.onerror = () => resolve(null);
    });
  };

  const handleUpload = async () => {
    if (!file) return;
    setErr('');
    setUploading(true);
    setProgress(0);
    try {
      const duration = await detectDuration(file);
      const res = await api.uploadVideo(file, { label, club, view_angle: view, duration }, setProgress);
      onUploaded({
        ...res,
        original_filename: file.name,
        duration_seconds: duration,
        label, club, view_angle: view,
        created_at: new Date().toISOString()
      });
    } catch (e) {
      setErr(e.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3 max-w-md mx-auto">
      <label className="block">
        <span className="text-[11px] uppercase tracking-wider text-ink-400 block mb-1">Video file</span>
        <input
          type="file"
          accept="video/*"
          onChange={(e) => setFile(e.target.files[0])}
          className="block w-full text-sm text-ink-200 file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-fairway-800 file:text-ink-100 file:text-xs file:uppercase file:tracking-wider hover:file:bg-fairway-700 cursor-pointer"
        />
      </label>
      <label className="block">
        <span className="text-[11px] uppercase tracking-wider text-ink-400 block mb-1">Label</span>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Driver, Oct 18"
          className="w-full px-3 py-1.5 bg-fairway-950 border border-fairway-700 rounded text-sm text-ink-100 focus:outline-none focus:border-cream-300"
        />
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-ink-400 block mb-1">Club</span>
          <select
            value={club}
            onChange={(e) => setClub(e.target.value)}
            className="w-full px-3 py-1.5 bg-fairway-950 border border-fairway-700 rounded text-sm text-ink-100 focus:outline-none focus:border-cream-300"
          >
            <option value="">—</option>
            <option>Driver</option>
            <option>3-wood</option>
            <option>Hybrid</option>
            <option>Long iron</option>
            <option>Mid iron</option>
            <option>Short iron</option>
            <option>Wedge</option>
            <option>Putter</option>
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-ink-400 block mb-1">View</span>
          <select
            value={view}
            onChange={(e) => setView(e.target.value)}
            className="w-full px-3 py-1.5 bg-fairway-950 border border-fairway-700 rounded text-sm text-ink-100 focus:outline-none focus:border-cream-300"
          >
            <option value="">—</option>
            <option>Down-the-line</option>
            <option>Face-on</option>
            <option>Overhead</option>
          </select>
        </label>
      </div>

      {uploading && (
        <div>
          <div className="h-2 bg-fairway-800 rounded overflow-hidden">
            <div className="h-full bg-cream-200 transition-all" style={{ width: `${progress * 100}%` }} />
          </div>
          <div className="text-[11px] text-ink-400 mt-1 monospace">{Math.round(progress * 100)}%</div>
        </div>
      )}

      {err && <div className="text-accent-flag text-xs">{err}</div>}

      <button
        onClick={handleUpload}
        disabled={!file || uploading}
        className="w-full py-2 bg-cream-100 text-fairway-950 rounded text-sm font-medium hover:bg-cream-200 disabled:opacity-30 transition"
      >
        {uploading ? 'Uploading…' : 'Upload'}
      </button>
    </div>
  );
}
